<template>
  <div>
    <div class="logout" v-if="userID">
      <a href="#" @click="logout">Log out</a>
    </div>

    <div :class="'artist-new' + (userID ? ' logged-in' : '')">
      <header>
        <h1>Austin Studio Tour {{$root.year}}</h1>
      </header>

      <section class="card" v-if="step === 'loading'">
        <h2>Please wait...</h2>
        <p>Loading your existing application.</p>
        <p>Not you? <a href="#" @click="logout">Log out</a></p>
      </section>

      <section class="card" v-if="step === 'login'">
        <h2 v-if="!createAccount">Log in</h2>
        <h2 v-else>Create an Account</h2>
        <p v-if="createAccount">Create an account so you can pick up where you leave off.</p>
        <p v-if="createAccount">Already started? <a href="#" @click="createAccount = false">Log in</a></p>

        <div v-on:keyup.enter="login">
          <div class="field">
            <div class="input">
              <label class="input-label">Username</label>
              <input type="text" autocomplete="none" v-model="username" class="input-field">
            </div>
          </div>
          <div class="field">
            <div class="input">
              <label class="input-label">Password</label>
              <input type="password" autocomplete="current-password" v-model="password" class="input-field">
            </div>
            <p v-if="!createAccount">Forgot password? <a href="mailto:info@bigmedium.org?subject=Forgot Password" target="_blank">Email us</a>.</p>
          </div>

          <div class="submit single">
            <button :class="'btn' + (loading ? ' loading' : '')" @click="login" :disabled="loading">Log in</button>
          </div>
        </div>
      </section>

      <section class="card" v-if="step === 0">
        <h2>Creative and Business Partners</h2>
        <br/>
        <p>Creative and Business Partners (CBP) are local design and architectural firms, restaurants, coffee shops, retail spaces, galleries, showrooms, and other businesses, within the tour boundaries, that directly support the Austin Studio Tour by purchasing a branded listing in the tour catalog, website, and mobile app: the go-to resources for creatives, makers, artists, collectors, and art enthusiasts. These organizations directly support the local art community by providing vital funds that keep tour programming free and accessible to the public. Additionally, CBPs can provide innovative ways to exhibit and promote artists and their businesses. In turn, Big Medium celebrates your brand as a supporter of the incredible art and artists that our city has to offer. We direct our audience, including participants on the tour, to support your endeavors year-round.</p>
        <br />
        <br />
        <p><strong>As part of your support for the Austin Studio Tour, all Creative and Business Partners receive:</strong></p>
        <ul>
          <li>Official graphics to help you promote your participation on your channels</li>
          <li>Official Austin Studio Tour “yard sign” recognizing Partners</li>
          <li>Listing in the Austin Studio Tour catalog (2,000 printed and distributed), map (10,000 printed and distributed), website and digital map (100,000+ unique visitors), and mobile app (12,000 users and growing!)</li>
          <li>Social media recognition on Instagram (24,000+ followers)</li>
          <li>15% discount on advertising in the catalog. Reserve your ad space <a href="https://www.bigmedium.org/ast24-advertise-open-call" target="_blank">here</a>.</li>
        </ul>
        <br/>
        <p><strong>Rate:</strong> $500, includes a featured image</p>
        <br/>
        <br/>
        <p>If you have any questions or are interested in Advertising in the Austin Studio Tour catalog, please contact Coka Treviño at <a href="mailto:trevino@bigmedium.org">trevino@bigmedium.org</a>.</p>

        <div class="submit single">
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep">Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 1">
        <h2>Point of Contact</h2>
        <p>For internal use only. Please let us know who to be in touch with.</p>

        <div class="field">
          <div class="input">
            <label class="input-label">First Name<span class="required">*</span></label>
            <input type="text" v-model="submission.first_name" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Last Name<span class="required">*</span></label>
            <input type="text" v-model="submission.last_name" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Email<span class="required">*</span></label>
            <input type="email" inputmode="url" autocomplete="email" v-model="submission.email" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Phone<span class="required">*</span></label>
            <input type="tel" inputmode="url" autocomplete="tel" v-model="submission.phone" class="input-field" @input="formatPhone">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Address 1<span class="required">*</span></label>
            <input type="text" autocomplete="street-address" v-model="submission.address" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Address 2</label>
            <input type="text" autocomplete="" v-model="submission.address_2" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">City<span class="required">*</span></label>
            <input type="text" autocomplete="address-level2" v-model="submission.city" class="input-field">
          </div>
        </div>

        <div class="field other" v-if="schema">
          <label class="input-label">State<span class="required">*</span></label>
          <select v-model="submission.state">
             <option v-for="option in schema.state.items.enum" :key="option" :value="option">{{ option }}</option>
          </select>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Zipcode<span class="required">*</span></label>
            <input type="number" autocomplete="postal-code" v-model="submission.zip" class="input-field">
          </div>
        </div>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep" :disabled="!step1Valid">Save and Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 2">
        <h2>About Your Brand</h2>
        <p>This information will be used for the public listing.</p>

        <div class="field">
          <div class="input">
            <label class="input-label">Brand Name<span class="required">*</span></label>
            <input type="text" v-model="submission.business_name" class="input-field">
          </div>
          <p>Please let us know how you would like to be listed in all Austin Studio Tour collateral.</p>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Brand Email</label>
            <input type="email" inputmode="url" autocomplete="email" v-model="submission.business_email" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Brand Phone</label>
            <input type="tel" inputmode="url" autocomplete="tel" v-model="submission.business_phone" class="input-field" @input="formatBusinessPhone">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Website</label>
            <input type="text" v-model="submission.business_website" class="input-field">
          </div>
          <p>i.e. bigmedium.org</p>
        </div>

        <h2>Brand Address</h2>

        <div class="field">
          <div class="input">
            <label class="input-label">Address 1</label>
            <input type="text" autocomplete="street-address" v-model="submission.tour_address" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Address 2</label>
            <input type="text" autocomplete="" v-model="submission.tour_address_2" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">City</label>
            <input type="text" autocomplete="address-level2" v-model="submission.tour_city" class="input-field">
          </div>
        </div>

        <div class="field other" v-if="schema">
          <label class="input-label">State</label>
          <select v-model="submission.tour_state">
            <option v-for="option in schema.state.items.enum" :key="option" :value="option">{{ option }}</option>
          </select>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Zipcode</label>
            <input type="number" autocomplete="postal-code" v-model="submission.tour_zip" class="input-field">
          </div>
        </div>

        <div class="field">
          <p>This location will be shown on the website and app. If the pin is not in the correct spot, click anywhere on the map to set your own position.</p>
        </div>

        <GmapMap
          ref="mapRef"
          @click="mapClick"
          :center="submission.coordinates ? { lat: +submission.coordinates.split(',')[0], lng: +submission.coordinates.split(',')[1] } : { lat: 30.275, lng: -97.745 }"
          :options="{
            disableDefaultUI: true,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            zoom: submission.coordinates ? 16 : 12.5,
          }"
        >
          <gmap-marker
            v-if="submission.coordinates"
            :position="{ lat: +submission.coordinates.split(',')[0], lng: +submission.coordinates.split(',')[1] }"
            :icon="mapIcon"
          ></gmap-marker>
        </GmapMap>
        <button v-if="didUseMap" :class="'btn'" @click="resetMap">Reset</button>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep" :disabled="!step2Valid">Save and Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 3">
        <h2>Preferred Social Media </h2>
        <p>Please select your preferred social media and list your handle. Due to space limitations, we can only include one handle.</p>

        <div class="field">
          <div class="input">
            <label class="input-label">Instagram (@___)</label>
            <input type="text" v-model="submission.instagram" class="input-field">
          </div>
          <p>i.e. @bigmedium</p>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Facebook (facebook.com/___)</label>
            <input type="text" v-model="submission.facebook" class="input-field">
          </div>
          <p>i.e. facebook.com/bigmediumaustin</p>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Twitter (@___)</label>
            <input type="text" v-model="submission.twitter" class="input-field">
          </div>
          <p>i.e. @bigmedium</p>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">TikTok</label>
            <input type="text" v-model="submission.tiktok" class="input-field">
          </div>
          <p>i.e. @bigmedium</p>
        </div>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep" :disabled="!step3Valid">Save and Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 4">
        <h2>Brand Info</h2>

        <div class="field other">
          <label class="input-label">Category<span class="required">*</span></label>
          <div class="input-radio">
            <label>
              <input type="checkbox" value="Eat/Drink" v-model="submission.category">
              <span>Eat/Drink<br> Restaurants, coffee shops, bars, caterers, etc.</span>
            </label>
            <label>
              <input type="checkbox" value="Explore" v-model="submission.category">
              <span>Explore<br> Architecture and design firms, non retail showrooms, schools, publications, etc.</span>
            </label>
            <label>
              <input type="checkbox" value="Shop" v-model="submission.category">
              <span>Shop<br> Retail shops, showrooms, markets</span>
            </label>
            <label>
              <input type="checkbox" value="Other" v-model="submission.category">
              <span>Other<br> If you don’t feel any of the above categories represent your services, please select this option.</span>
            </label>
          </div>
          <p>This feature will be used as a filter on the website and app to help customers find you.</p>
        </div>

        <div class="field textarea">
          <label class="input-label">About<span class="required">*</span></label>
          <textarea v-model="submission.about"></textarea>
          <p :class="`char-limit${submission.about.match(/\b\S+\b/g) && submission.about.match(/\b\S+\b/g).length > 60 ? ' error' : ''}`">{{ submission.about.match(/\b\S+\b/g) ? submission.about.match(/\b\S+\b/g).length : 0 }} / 60</p>
          <p>Describe what your brand does year round. 60 words maximum.</p>
        </div>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep" :disabled="!step4Valid">Save and Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 5">
        <h2>Questions</h2>

        <div class="field textarea">
          <label class="input-label">Will you be hosting any artists, either physically in your space?<span class="required">*</span></label>
          <br />
          <br />
          <p>If you have identified the artists participating in your space, please provide their name and email address so that we can connect them to you on your page.</p>
          <br />
          <p>If you’d like Big Medium to connect you with artists, please make that selection and comment if they’re paying for their fee or if you are, keep in mind that if you require of pairing, the deadline to submit this application and request is July 20 27 since Big Medium would need to find artists, and they would have to apply before the July 31 deadline.</p>
          <br />
          <p>If you are not hosting artists, please enter N/A.</p>
          <br />
          <textarea v-model="submission.hosting"></textarea>
          <p :class="`char-limit${submission.hosting.match(/\b\S+\b/g) && submission.hosting.match(/\b\S+\b/g).length > 60 ? ' error' : ''}`">{{ submission.hosting.match(/\b\S+\b/g) ? submission.hosting.match(/\b\S+\b/g).length : 0 }} / 60</p>
          <br />
          <p>60 words maximum.</p>
        </div>

        <div class="field other" v-if="schema">
          <label class="input-label">If you are not already hosting artists, are you interested in being connected to artists looking for venues?<span class="required">*</span></label>
          <div class="input-radio">
            <label v-for="option in schema.hosting_interested.enum" :key="option">
              <input type="radio" :value="option" v-model="submission.hosting_interested">
              <span>{{ option }}</span>
            </label>
          </div>
        </div>

        <div class="field other" v-if="schema && submission.hosting_interested.includes('Yes, please connect me to artists')">
          <label class="input-label">I am interested in</label>
          <div class="input-radio">
            <label v-for="option in schema.hosting_interests.items.enum" :key="option">
              <input type="checkbox" :value="option" v-model="submission.hosting_interests">
              <span>{{ option }}</span>
            </label>
          </div>
        </div>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep" :disabled="!step5Valid">Save and Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 6">
        <h2>Attachments</h2>
        <p>This image will be used on the Austin Studio Tour website, app, catalog and social media.</p>

        <div class="field other">
          <label class="input-label">Image for Featured Listing<span class="required">*</span></label>
          <input v-if="!submission.featured_image" type="file" class="input-file" accept=".jpg,.jpeg,.pdf,.tiff" @change="prepareUploadFile($event, 'featured_image')">
          <div v-else class="file-info">
            <a :href="submission.featured_image.url" target="_blank">{{ submission.featured_image.url }}</a>
            <button class="delete" @click="deleteFile($event, 'featured_image')">Delete</button>
          </div>
          <p>Image specifications: Horizontal orientation preferred, set resolution at 300 PPI / DPI, color mode set to CMYK. Accepted file formats are JPG, PDF and TIFF. NO LOGOS PLEASE.</p>
        </div>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep" :disabled="!step6Valid">Save and Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 7">
        <h2>Terms of Use</h2>
        <p>While we encourage tour-goers to frequent the generous businesses that sign up to support the tour, we strongly recommend that our Creative and Business Partners promote their participation and presence through their own websites and social media platforms as well. Big Medium will provide Austin Studio Tour graphics package for you to use at your discretion. Designation as a Creative and Business Partner does not guarantee an increase in sales during the days of the event. Thank you for being a part of the tour — we are grateful for your support of the local arts community!</p>

        <div class="field other" v-if="schema">
          <label class="input-label">I agree with Terms of Use<span class="required">*</span></label>
          <div class="input-radio">
            <label v-for="option in schema.agreement.enum" :key="option">
              <input type="radio" :value="option" v-model="submission.agreement">
              <span>{{ option }}</span>
            </label>
          </div>
        </div>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep" :disabled="!step7Valid">Save and Continue</button>
        </div>
      </section>

      <section class="card" v-if="step === 8 && submission.paid">
        <h2>Complete</h2>
        <p>You have already submitted this application and edits have been saved. You may now close this window.</p>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
        </div>
      </section>

      <section class="card" v-else-if="step === 8">
        <h2>Extras</h2>
        <p>Your application fee includes 2 catalogs and 1 sign. If you would like extra items, you may purchase additional catalogs for $20 each and signs for $12 each. Please identify below how many of each item you would like to purchase.</p>

        <div class="field">
          <div class="input">
            <label class="input-label">Catalogs</label>
            <input type="number" min="0" v-model="submission.catalogs" class="input-field">
          </div>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Signs</label>
            <input type="number" min="0" v-model="submission.signs" class="input-field">
          </div>
        </div>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <button :class="'btn' + (loading ? ' loading' : '')" @click="nextStep">Save and Continue</button>
        </div>
      </section>

      <section class="card final" v-if="step === 9">
        <h2>Your total is ${{ submission.total }}</h2>

        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
              <th>Unit price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Submission fee</td>
              <td>1</td>
              <td>${{ submission.submission_fee }}</td>
              <td>${{ submission.submission_fee * 1 }}</td>
            </tr>
            <tr>
              <td>Catalog</td>
              <td>{{ submission.catalogs || 0 }}</td>
              <td>$20</td>
              <td>${{ submission.catalogs * 20 }}</td>
            </tr>
            <tr>
              <td>Sign</td>
              <td>{{ submission.signs || 0 }}</td>
              <td>$12</td>
              <td>${{ submission.signs * 12 }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              <th><strong>${{ submission.total }}</strong></th>
            </tr>
          </tfoot>
        </table>

        <div class="input-radio">
          <label>
            <input type="checkbox" value="yes" v-model="submission.cover_fees" @click="coverFees">
            <span for="yes">Add 3% to your total to help us cover processing fees.</span>
          </label>
        </div>

        <div class="field">
          <div class="input">
            <label class="input-label">Promo code</label>
            <input type="text" v-model="submission.promo" class="input-field">
          </div>
          <p>Have a promo code?</p>
        </div>
        <button :class="'btn' + (promoLoading ? ' loading' : '')" @click="verifyPromo">Verify</button>

        <div class="submit">
          <button class="btn" @click="prevStep">Back</button>
          <form v-if="submission.total > 0" :action="$root.api_url + 'checkout?userType=' + type + '&id=' + userID + '&total=' + submission.total" method="POST">
            <button :class="'btn' + (loading ? ' loading' : '')">Proceed to Checkout</button>
          </form>
          <button v-else :class="'btn' + (loading ? ' loading' : '')" @click="finish">Finish</button>
        </div>
      </section>

      <section class="card final" v-if="step === 'finish'">
        <h2>Thank you! Your submission has been received.</h2>
        <p>You may now close this window.</p>
      </section>
      <section class="card" v-if="step === 'checkout_verify'">
        <h2>Verifying payment...</h2>
      </section>
      <section class="card" v-if="step === 'checkout_success'">
        <h2>Thank you! Payment has been received</h2>
        <p>Thank you for becoming a Creative and Business Partner for the Austin Studio Tour {{$root.year}}!</p>
        <p>Your commitment to supporting our local arts community with your participation ensures that our creative sector is celebrated and assisted in many ways during this fall. We will be in touch soon to confirm your listing.</p>
        <br>
        <p>Should you have any questions regarding your participation as a Partner, or are interested in advertising in the catalog, please reach out to Coka Treviño at <a href="mailto:trevino@bigmedium.org">trevino@bigmedium.org</a>.</p>
        <br>
        <p>Warmly,<br><br>The Big Medium Team</p>
      </section>
      <section class="card" v-if="step === 'checkout_error'">
        <h2>There was an error processing payment</h2>
        <p>Click the link below to restart.</p>
        <button class="btn" @click="reset">Try Again</button>
      </section>

      <footer v-if="submission && !submission.paid">
        <p v-if="step > 0">Step <span>{{ step }}</span> of <span>{{ totalSteps }}</span></p>
      </footer>
    </div>
  </div>
</template>

<script>
import Forms from "./Forms";

export default {
  name: 'bp',
  components: {},
  mixins: [Forms],
  data() {
    return {
      totalSteps: 9,
      step1Valid: false,
      step2Valid: false,
      step3Valid: false,
      step4Valid: false,
      step5Valid: false,
      step6Valid: false,
      step7Valid: false,
    };
  },
  mounted() {
    if (this.$route.query.checkout_success) {
      this.step = 'checkout_verify';
      this.verifyCheckoutSuccess();
    } else if (this.$route.query.checkout_cancel) {
      this.step = 'loading';
      this.login(null, 9);
    } else {
      if (localStorage.getItem('userID')) {
        this.step = 'loading';
        this.login();
      }
    }
  },
  methods: {
    formatPhone() {
      let phoneNumber = this.submission.phone.replace(/\D/g, '');
      if (phoneNumber.length > 10) {
        phoneNumber = phoneNumber.slice(0, 10);
      }
      let formattedNumber = phoneNumber.replace(/(\d{3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
      this.submission.phone = formattedNumber;
    },
    formatBusinessPhone() {
      let phoneNumber = this.submission.business_phone.replace(/\D/g, '');
      if (phoneNumber.length > 10) {
        phoneNumber = phoneNumber.slice(0, 10);
      }
      let formattedNumber = phoneNumber.replace(/(\d{3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
      this.submission.business_phone = formattedNumber;
    },

    validate() {
      this.step1Valid = false;
      if (
        this.submission.first_name.length > 0 &&
        this.submission.last_name.length > 0 &&
        this.submission.email.length > 0 &&
        this.submission.phone.length > 0 &&
        this.submission.address.length > 0 &&
        this.submission.city.length > 0 &&
        this.submission.state.length > 0 &&
        this.submission.zip.length > 0
      ) {
        this.step1Valid = true;
      }

      this.step2Valid = false;
      if (this.submission.business_name.length > 0) {
        this.step2Valid = true;
      }

      this.step3Valid = true;

      this.step4Valid = false;
      if (
        this.submission.category.length > 0 &&
        this.submission.about.length > 0 &&
        (this.submission.about.match(/\b\S+\b/g) && this.submission.about.match(/\b\S+\b/g).length < 61)
      ) {
        this.step4Valid = true;
      }

      this.step5Valid = false;
      if (
        this.submission.hosting.length > 0 &&
        (this.submission.hosting.match(/\b\S+\b/g) && this.submission.hosting.match(/\b\S+\b/g).length < 61) &&
        this.submission.hosting_interested.length > 0
      ) {
        this.step5Valid = true;
      }

      this.step6Valid = false;
      if (this.submission.featured_image) {
        this.step6Valid = true;
      }

      this.step7Valid = false;
      if (this.submission.agreement === 'Yes') {
        this.step7Valid = true;
      }
    },

    reset() {
      location.href = `/#/bp-app`;
      location.reload();
    },
  }
};
</script>

<style scoped lang="less">
  html, body {
    width: auto;
    height: auto;
    overflow: initial;
    -webkit-user-select: initial;
    background-color: #F7F6F3;
  }
</style>
<style src="./Forms.less" lang="less" scoped></style>



// WEBPACK FOOTER //
// src/pages/Forms/BP.vue